import * as React from "react";
import type { To } from "react-router";
import type { IconName } from "../../icons";
import { icons } from "../../icons";
import {
  ButtonOrLink,
  type ExclusiveUnion,
} from "../ButtonOrLink/ButtonOrLink";
import styles from "./Button.css";

const sizeMapIcon = {
  small: "16px",
  medium: "24px",
  large: "24px",
} as const;

type Size = keyof typeof sizeMapIcon;
type Color = "neutral" | "accent" | "success" | "error";
type Variant = "primary" | "secondary";

// What the button renders follows from `to` / `href`, mirroring the shapes in
// `ButtonOrLink` — just with the short prop list this component offers.
type AsButton = {
  type?: "button" | "submit";
  /** Id of the form to submit when the button isn't nested in it, e.g. a Dialog footer's. */
  form?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

type AsRouterLink = {
  to: To;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type AsAnchor = {
  href: string;
  // `rel` defaults to "noopener noreferrer" for `target="_blank"`.
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type ButtonProps = {
  title: string;
  size?: Size;
  color?: Color;
  variant?: Variant;
  iconLeft?: IconName;
  iconRight?: IconName;
  fullWidth?: boolean;
  "aria-current"?: React.AriaAttributes["aria-current"];
} & ExclusiveUnion<AsButton | AsRouterLink | AsAnchor>;

const Button = ({
  title,
  size = "medium",
  color = "neutral",
  variant = "primary",
  iconLeft,
  iconRight,
  fullWidth = false,
  ...rest
}: ButtonProps) => {
  const IconLeft = iconLeft ? icons[iconLeft] : null;
  const IconRight = iconRight ? icons[iconRight] : null;

  return (
    <ButtonOrLink
      {...rest}
      className={styles.button({ size, color, variant, fullWidth })}
    >
      {IconLeft && <IconLeft size={sizeMapIcon[size]} />}
      {title}
      {IconRight && <IconRight size={sizeMapIcon[size]} />}
    </ButtonOrLink>
  );
};

export default Button;
